import React, { useRef } from "react";

import { renderBlockDescriptionHtml, renderBlockTitleHtml } from "./blockDescriptionHtml";
import { DashboardWindow } from "./dashboardHelpers";
import { useTheme } from "./themes";
import { ErrorStateDetails, useBlockRender } from "./useBlockRender";
import { SlicerState } from "./useDashboardRuntime";

interface Props {
  blockId: string;
  title: string;
  description?: string | null;
  /**
   * Signature of the block's `chart_overrides`. Every override reaches the chart through a
   * rebuild, so ANY change here — including a key being removed — re-runs the render effect
   * and createChart re-applies the whole set. The render JS itself is unchanged by a manual
   * edit, so nothing else would trigger the re-render.
   */
  chartOverridesSignature?: string;
  renderFunctionJs: string;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix?: string;
  blockType: string;
  // BE-provided pre-render error attribution (e.g. missing source data). When
  // set, the error fallback shows this string verbatim in place of the generic
  // "Unable to display chart" title and hides the "Learn more" button.
  errorState?: string | null;
  // Companion to `errorState`: open dict of structured attribution details
  // (currently `{ tabs?: string[] }` for source tabs, rendered as a tooltip).
  errorStateDetails?: ErrorStateDetails | null;
  onRendered?: (blockId: string) => void;
  onRenderStart?: (blockId: string) => void;
  onRenderComplete?: (blockId: string) => void;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Wired to the "Learn more" button in the error fallback. */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

export const ChartBlock: React.FC<Props> = ({
  blockId,
  title,
  description,
  chartOverridesSignature,
  renderFunctionJs,
  slicerState,
  targetWindow,
  idSuffix = "",
  blockType,
  errorState,
  errorStateDetails,
  onRendered,
  onRenderStart,
  onRenderComplete,
  onFixBlock,
  onExplainBlockInChat,
}) => {
  const { themeSignature } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);

  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;

  const escapedTitle = title.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const escapedDescription = description
    ? description.replace(/</g, "&lt;").replace(/>/g, "&gt;")
    : null;

  useBlockRender({
    cardRef,
    // Mobile puts the description's info icon inline to the right of the title (Figma 5753:13777):
    // 8px gap, centre-aligned, 12px below. Exact px rather than gap-2/pb-3 because the iframe's
    // 14px root would scale those to 7px/10.5px. Desktop keeps no class at all on the wrapper, so
    // the h2-then-paragraph stacking is byte-identical to before.
    //
    // The title is held to one line on phones, matching DataTableBlock. A second line would push the
    // chart canvas down and misalign cards sharing a row — `themed-card-title` is flex-shrink-0 with
    // mb-4, so it steals height rather than absorbing it. Desktop keeps wrapping so long titles stay
    // fully readable. renderBlockTitleHtml adds the mobile tap-to-reveal popover that makes the
    // truncated text recoverable; it and the description share `fullBlockId` as their disclosure
    // group so only one of the two can be open at a time.
    initialInnerHtml: `<div class="block-title-row max-sm:relative max-sm:flex max-sm:items-center max-sm:gap-[8px] max-sm:pb-[12px]">${renderBlockTitleHtml(
      escapedTitle,
      fullBlockId
    )}${
      escapedDescription ? renderBlockDescriptionHtml(escapedDescription, fullBlockId) : ""
    }</div><div class="chart-container flex-1 min-h-0" data-sentry-block><canvas class="chart-canvas w-full h-full"></canvas></div>`,
    renderFunctionJs,
    slicerState,
    targetWindow,
    fullBlockId,
    blockId,
    blockType,
    blockTitle: title,
    errorState,
    errorStateDetails,
    themeSignature,
    overridesSignature: chartOverridesSignature,
    onRendered,
    onRenderStart,
    onRenderComplete,
    onFixBlock,
    onExplainBlockInChat,
  });

  return (
    // data-coeff-block-id lets createChart look up this block's persisted chart_overrides
    // (same contract as DataTableBlock's card).
    <div
      ref={cardRef}
      id={fullBlockId}
      data-coeff-block-id={blockId}
      className="themed-dashboard-card"
    />
  );
};
